import { ResourceSummaryDto, TimeSeriesDataPointDto } from './types';
import { DeviceUsageSnapshotResponse, InventoryChangesTrendResponse, InventorySnapshotResponse, MeetingRoomDeviceUsageSnapshotResponse } from '@hooks/utilisation/types';
import { LicensesAssignmentResponse, LicensesActivityResponse, LicensesRedundantResponse, LicensesExpiredResponse, LicensesTrendResponse, LicensesExpiringResponse, LicensesUsageTrendResponse, LicensesExpiringVendorResponse, LicencesExpiredBarChartResponse } from '@hooks/licenses/types';
import { MeetingRoomTrendCompareAgainstResponse, NumberSnapshotResponse, TrendDataItem, UserPercentageResponse } from '@common/types';
import { ExternalAppsMSDataResponse, ExternalTeamsAccessResponse, ExternalAppsAccountLevelResponse, ExternalAppsPublisherVerificationResponse, ExternalAppsTenantLevelResponse, ExternalUsersAccessResponse, MfaStatusSnapshotResponse, MsSecureScoreSnapshotResponse, SecureScoreTypesSnapshotResponse, SuspiciousActivitySourceCountryResponse, SuspiciousActivityTotalEventResponse, MailboxesForwardingSnapshotResponse } from '@hooks/security/types';
import { ServiceAdoptionSnapshotResponse, TeamsGroupsAdoptionSnapshotResponse, CollabSnapshotResponse, MsTeamsHealthSnapshotResponse, UserServiceAdoptionSnapshotResponse, UserServiceAdoptionTrendResponse, DeviceAdoptionSnapshotResponse, MeetingAdoptionSnapshotResponse, MeetingAdoptionTrendResponse, InactiveRoomsSnapshotResponse, MeetingRoomsSnapshotResponse, MeetingsRoomBookedHoursResponse, GroupsActivitySnapshotResponse, OrgGroupActivityUserSnapshotResponse } from '@hooks/adoption/types';
import { ActivityPerDayTrendResponse } from '@hooks/productivity/types';
import { ExternalTeamsAccessResponse as DelegatedMailboxResponse } from '@hooks/security/types';
import { FailedLoginAccountsSnapshotResponse, FailedLoginDataResponse } from '@hooks/failedLoginData/types';
import { OutdatedOSSActivitynapshotResponse } from '@hooks/outdatedData/types';

// ?? Shared helpers ???????????????????????????????????????????????????

/** Safely reads a nested dict from aggregates. Returns empty object if missing. */
function getNestedDict(s: ResourceSummaryDto, key: string): Record<string, unknown> {
  const v = s.aggregates[key];
  return (v && typeof v === 'object' && !Array.isArray(v)) ? v as Record<string, unknown> : {};
}

/** Safely reads a nested array from aggregates. Returns undefined if missing. */
function getNestedArray(s: ResourceSummaryDto, key: string): unknown[] | undefined {
  const v = s.aggregates[key];
  return Array.isArray(v) ? v as unknown[] : undefined;
}

/** Safely reads a nested dict-of-dict value: aggregates[outerKey][innerKey] */
function getNestedValue(s: ResourceSummaryDto, outerKey: string, innerKey: string): unknown {
  const outer = getNestedDict(s, outerKey);
  return outer[innerKey];
}

/** Reads a named hierarchy from the hierarchies bag. Returns empty object if missing. */
function getHierarchy(s: ResourceSummaryDto | undefined, key: string): Record<string, unknown> {
  if (!s?.hierarchies) return {};
  const v = s.hierarchies[key] ?? Object.entries(s.hierarchies).find(([k]) => k.toLowerCase() === key.toLowerCase())?.[1];
  return (v && typeof v === 'object' && !Array.isArray(v)) ? v as Record<string, unknown> : {};
}

/** Reads a time-series array from a hierarchy entry's "Series" field. */
function getHierarchySeries(s: ResourceSummaryDto | undefined, hierarchyKey: string, metricName: string): TimeSeriesDataPointDto[] | undefined {
  const hierarchy = getHierarchy(s, hierarchyKey);
  const metric = getCaseInsensitive(hierarchy, metricName);
  if (!metric || typeof metric !== 'object') return undefined;
  const series = (metric as Record<string, unknown>)['Series'] ?? (metric as Record<string, unknown>)['series'];
  if (!Array.isArray(series)) return undefined;

  return series
    .map((rawPoint) => {
      if (!rawPoint || typeof rawPoint !== 'object') return undefined;
      const point = rawPoint as Record<string, unknown>;
      const timestamp = getCaseInsensitive(point, 'timestamp');
      const value = getCaseInsensitive(point, 'value');

      if (typeof timestamp !== 'string') return undefined;

      const numericValue = Number(value ?? Number.NaN);
      if (!Number.isFinite(numericValue)) return undefined;

      return {
        timestamp,
        value: numericValue,
      };
    })
    .filter((point): point is TimeSeriesDataPointDto => !!point);
}

/** Safely reads pre-computed shapes emitted by the summary pipeline. */
function getPrecomputedShape(
  s: ResourceSummaryDto | undefined,
  outerKey: string,
  innerKey?: string,
): Record<string, unknown> | undefined {
  if (!s) return undefined;

  const outerRaw = getCaseInsensitive(s.aggregates, outerKey);
  const outer = (outerRaw && typeof outerRaw === 'object' && !Array.isArray(outerRaw))
    ? outerRaw as Record<string, unknown>
    : {};
  if (!innerKey) return Object.keys(outer).length ? outer : undefined;

  const inner = getCaseInsensitive(outer, innerKey);
  return inner && typeof inner === 'object' && !Array.isArray(inner) ? inner as Record<string, unknown> : undefined;
}

function getCaseInsensitive(record: Record<string, unknown>, key: string): unknown {
  if (Object.prototype.hasOwnProperty.call(record, key)) return record[key];
  const match = Object.keys(record).find((k) => k.toLowerCase() === key.toLowerCase());
  return match ? record[match] : undefined;
}

function getWindowedShapeBucket(shape: Record<string, unknown> | undefined, days: string): Record<string, unknown> | undefined {
  if (!shape) return undefined;
  const dayValue = Math.max(1, Number(days) || 30);
  return (getCaseInsensitive(shape, `day${dayValue}`)
    ?? getCaseInsensitive(shape, String(dayValue))) as Record<string, unknown> | undefined;
}

function getWindowedShapeNumber(shape: Record<string, unknown> | undefined, days: string): number | undefined {
  if (!shape) return undefined;
  const dayValue = Math.max(1, Number(days) || 30);
  const value = getCaseInsensitive(shape, `day${dayValue}`) ?? getCaseInsensitive(shape, String(dayValue));
  return value === undefined ? undefined : Number(value ?? 0);
}

function isAllSelection(value: string | undefined, aliases: string[] = []): boolean {
  if (!value) return true;
  const normalized = value.trim().toLowerCase();
  if (normalized.length === 0) return true;
  if (normalized === 'all') return true;
  return aliases.some((alias) => alias.toLowerCase() === normalized);
}

function toTrendDataItems(points: TimeSeriesDataPointDto[] | undefined): TrendDataItem[] {
  if (!points?.length) return [];
  return normalizeDailySeriesWithGaps(points).map((p) => ({ date: p.timestamp, value: p.value }));
}

function toCountryDisplayName(countryOrCode: string): string {
  const trimmed = countryOrCode.trim();
  const isRegionCode = /^[A-Za-z]{2}$/.test(trimmed);
  if (!isRegionCode) return countryOrCode;

  try {
    const displayName = new Intl.DisplayNames(['en'], { type: 'region' }).of(trimmed.toUpperCase());
    return displayName || countryOrCode;
  } catch {
    return countryOrCode;
  }
}

function normalizeDailySeriesWithGaps(points: TimeSeriesDataPointDto[] | undefined): TimeSeriesDataPointDto[] {
  if (!points?.length) return [];

  const byDate: Record<string, number> = {};
  for (const p of points) {
    if (!p || typeof p.timestamp !== 'string') continue;
    const dateKey = p.timestamp.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) continue;
    byDate[dateKey] = Number(p.value ?? Number.NaN);
  }

  const sortedKeys = Object.keys(byDate).sort();
  if (!sortedKeys.length) return [];
  const start = new Date(`${sortedKeys[0]}T00:00:00Z`);
  const lastDataKey = sortedKeys[sortedKeys.length - 1];
  // Normalize only across observed data bounds; do not extend to today.
  const endKey = lastDataKey;
  const end = new Date(`${endKey}T00:00:00Z`);

  const normalized: TimeSeriesDataPointDto[] = [];
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    const key = d.toISOString().slice(0, 10);
    normalized.push({
      timestamp: `${key}T00:00:00Z`,
      value: Object.prototype.hasOwnProperty.call(byDate, key) ? byDate[key] : Number.NaN,
    });
  }

  return normalized;
}

function extendSeriesToTrailingWindow(
  points: TimeSeriesDataPointDto[],
  targetDays: number,
  endDateOverride?: string,
): TimeSeriesDataPointDto[] {
  if (!points.length) return [];

  const byDate: Record<string, number> = {};
  for (const p of points) {
    byDate[p.timestamp.slice(0, 10)] = p.value;
  }

  const sortedKeys = Object.keys(byDate).sort();
  const lastDataKey = sortedKeys[sortedKeys.length - 1];
  const candidateKey = endDateOverride ?? lastDataKey;
  const endKey = candidateKey;
  const end = new Date(`${endKey}T00:00:00Z`);
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (targetDays - 1));

  const extended: TimeSeriesDataPointDto[] = [];
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    const key = d.toISOString().slice(0, 10);
    extended.push({
      timestamp: `${key}T00:00:00Z`,
      value: Object.prototype.hasOwnProperty.call(byDate, key) ? byDate[key] : Number.NaN,
    });
  }

  return extended;
}

export function extractTrend(
  s: ResourceSummaryDto | undefined,
  seriesKey: string
): TrendDataItem[] | undefined {
  const points = s?.timeSeries?.[seriesKey];
  if (!points?.length) return undefined;
  return toTrendDataItems(points);
}

function toWindowedTrendFromSeriesKeys(
  s: ResourceSummaryDto | undefined,
  seriesKeys: string[],
  days: string,
  endOffsetDays: number = 0,
): TrendDataItem[] | undefined {
  if (!s || seriesKeys.length === 0) return undefined;

  const byDate: Record<string, number> = {};
  let hasPoints = false;

  for (const seriesKey of seriesKeys) {
    const points = s.timeSeries?.[seriesKey];
    if (!points?.length) continue;

    for (const point of points) {
      if (!point || typeof point.timestamp !== 'string') continue;
      const dateKey = point.timestamp.slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) continue;
      hasPoints = true;
      byDate[dateKey] = (byDate[dateKey] ?? 0) + Number(point.value ?? 0);
    }
  }

  if (!hasPoints) return undefined;

  const windowDays = Math.max(1, Number(days) || 30);
  const sortedKeys = Object.keys(byDate).sort();
  const lastDataKey = sortedKeys[sortedKeys.length - 1];
  if (!lastDataKey) return undefined;

  const end = new Date(`${lastDataKey}T00:00:00Z`);
  if (endOffsetDays > 0) {
    end.setUTCDate(end.getUTCDate() - endOffsetDays);
  }
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (windowDays - 1));

  const trend: TrendDataItem[] = [];
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    const key = d.toISOString().slice(0, 10);
    trend.push({
      date: `${key}T00:00:00Z`,
      value: Object.prototype.hasOwnProperty.call(byDate, key) ? byDate[key] : null,
    });
  }

  return trend;
}

function getActivitySeriesKeys(activityType: string): string[] {
  switch (activityType.toLowerCase()) {
    case 'emails':
      return ['EmailsSent'];
    case 'messages':
      return ['MessagesSent'];
    case 'calls':
      return ['CallsMade'];
    case 'meetings':
      return ['MeetingsJoined'];
    case 'allactivities':
      return ['EmailsSent', 'MessagesSent', 'CallsMade', 'MeetingsJoined'];
    default:
      return ['EmailsSent'];
  }
}

function getUserServiceAdoptionSeriesKeys(dataType: string): string[] {
  switch (dataType.toLowerCase()) {
    case 'emails':
      return ['EmailsSent'];
    case 'messages':
      return ['MessagesSent'];
    case 'phone':
      return ['CallsMade'];
    case 'videocalls':
      return ['VideoCallsDuration'];
    case 'allservices':
      return ['EmailsSent', 'MessagesSent', 'CallsMade', 'VideoCallsDuration'];
    default:
      return ['MessagesSent'];
  }
}

function toUsingNotUsingSnapshot(
  source: Record<string, unknown> | undefined
): ServiceAdoptionSnapshotResponse | undefined {
  if (!source) return undefined;

  const usingRaw = getCaseInsensitive(source, 'usingPercentage') ?? getCaseInsensitive(source, 'UsingPercentage');
  const notUsingRaw = getCaseInsensitive(source, 'notUsingPercentage') ?? getCaseInsensitive(source, 'NotUsingPercentage');

  const using = Number(usingRaw ?? Number.NaN);
  const notUsing = Number(notUsingRaw ?? Number.NaN);

  if (Number.isFinite(using) && Number.isFinite(notUsing)) {
    return {
      usingPercentage: using,
      notUsingPercentage: notUsing,
    };
  }

  return undefined;
}

function resolveServiceAdoptionWindow(
  s: ResourceSummaryDto,
  days: string,
): Record<string, unknown> {
  const adoption = getNestedDict(s, 'ServiceAdoption');
  const byDays = getCaseInsensitive(adoption, days) as Record<string, unknown> | undefined;
  return byDays && typeof byDays === 'object' ? byDays : adoption;
}

function toCanonicalServiceAdoptionSnapshot(
  s: ResourceSummaryDto,
  serviceType: string,
  days: string,
): ServiceAdoptionSnapshotResponse | undefined {
  const normalized = serviceType.toLowerCase();
  const serviceKey =
    normalized === 'chat' ? 'messages'
      : normalized === 'video' ? 'videocalls'
      : normalized === 'phoneinternal' ? 'internalcalls'
      : normalized === 'phoneexternal' ? 'externalcalls'
      : normalized;

  const adoptionWindow = resolveServiceAdoptionWindow(s, days);
  const bucket = getCaseInsensitive(adoptionWindow, serviceKey) as Record<string, unknown> | undefined;
  const canonical = toUsingNotUsingSnapshot(bucket);
  if (canonical) return canonical;

  return undefined;
}

// ?? Utilisation page adapters ????????????????????????????????????????

/**
 * Licence inventory snapshot broken down by subscription name.
 *
 * Uses **two** summaries to match legacy behaviour exactly:
 *
 * "All" vendor path:
 *   Reads `Inventory_*` keys from the **Subscriptions** summary.
 *   These are computed by `ApplySubscriptionCustomLogic` as
 *   `GroupBy(Name).Sum(TotalLicenseCount)`  the same as the legacy
 *   `SubscriptionRepository.GetSubscriptionInventorySnapshotAsync`.
 *
 * Per-vendor path:
 *   Reads `VendorLicenses_{vendor}_*` keys from the **Licenses** summary
 *   (top-10 per vendor from pipeline custom logic).
 */
export function toLicenseInventorySnapshot(
  subscriptions: ResourceSummaryDto | undefined,
  licenses: ResourceSummaryDto | undefined,
  vendor?: string
): InventorySnapshotResponse | undefined {
  if (!subscriptions && !licenses) return undefined;
  if (!subscriptions) return undefined;

  const inventorySnapshot: Record<string, number> = {};

  if (!vendor || vendor === 'All') {
    const invNoFree = getNestedDict(subscriptions, 'InventoryNoFree');
    for (const [name, count] of Object.entries(invNoFree)) {
      inventorySnapshot[name] = Number(count ?? 0);
    }
      return Object.keys(inventorySnapshot).length ? { inventorySnapshot } : undefined;
  }

  // Vendor-scoped no-free inventory: Aggregates.InventoryNoFreeByVendor[vendor]
  const byVendor = getNestedDict(subscriptions, 'InventoryNoFreeByVendor');
  const vendorInv = byVendor[vendor];
  if (vendorInv && typeof vendorInv === 'object') {
    for (const [name, count] of Object.entries(vendorInv as Record<string, unknown>)) {
      inventorySnapshot[name] = Number(count ?? 0);
    }
  }
  return Object.keys(inventorySnapshot).length ? { inventorySnapshot } : undefined;
}


/**
 * License assignment snapshot (unassigned / assigned totals).
 * Uses Subscriptions summary aggregates: TotalLicenses_ExcludingFree and
 * AssignedLicenses_ExcludingFree to match the legacy API's includeFreeLicenses=false behavior.
 */
export function toLicensesAssignment(
  s: ResourceSummaryDto | undefined
): LicensesAssignmentResponse | undefined {
  if (!s) return undefined;

  const pre = getPrecomputedShape(s, 'Assignment');
  if (pre) {
    return {
      unassignedLicenses: Number(getCaseInsensitive(pre, 'unassignedLicenses') ?? getCaseInsensitive(pre, 'UnassignedLicenses') ?? 0),
      assignedLicenses: Number(getCaseInsensitive(pre, 'assignedLicenses') ?? getCaseInsensitive(pre, 'AssignedLicenses') ?? 0),
    };
  }

  const excludingFree = getNestedDict(s, 'ExcludingFree');
  const unassigned = Number(excludingFree['UnassignedLicenses'] ?? Number.NaN);
  const assigned = Number(excludingFree['AssignedLicenses'] ?? Number.NaN);
  if (!Number.isFinite(unassigned) || !Number.isFinite(assigned)) return undefined;

  return {
    unassignedLicenses: unassigned,
    assignedLicenses: assigned,
  };
}

/**
 * License activity snapshot (inactive / active totals).
 * days selects UsedLast30Days / UsedLast60Days / UsedLast90Days.
 * inactiveLicenses = total - active.
 */
/**
 * License activity snapshot (inactive / active totals).
 *
 * Legacy semantics: null LastSignInAt = NEITHER active nor inactive.
 * active   = UsedLast{days}Days   (non-null && recent)
 * inactive = InactiveLast{days}Days (non-null && stale)
 * The two do not sum to totalCount when null values exist.
 */
export function toLicensesActivity(
  s: ResourceSummaryDto | undefined,
  days: string = '30'
): LicensesActivityResponse | undefined {
  if (!s) return undefined;

  // Pipeline (License.Enrich.cs) emits:
  //   Aggregates["Activity"][`InactiveLast{days}Days`]  = paid licenses with stale LastSignInAt
  //   CountsByFilter[`UsedLast{days}Days`]              = paid licenses with recent LastSignInAt
  // (null LastSignInAt counts toward neither, matching legacy semantics.)
  const activityShape = s.aggregates?.['Activity'] as Record<string, unknown> | undefined;
  if (!activityShape) return undefined;

  const inactiveRaw = activityShape[`InactiveLast${days}Days`];
  if (inactiveRaw === undefined) return undefined;

  const inactiveFromShape = Number(inactiveRaw);
  const activeFromCounts = s.countsByFilter?.[`UsedLast${days}Days`];
  if (activeFromCounts === undefined) return undefined;

  const inactive = inactiveFromShape;
  const active = Number(activeFromCounts);

  if (!Number.isFinite(inactive) || !Number.isFinite(active)) return undefined;

  return {
    inactiveLicenses: inactive,
    activeLicenses: active,
  };
}

/**
 * Redundant licenses snapshot.
 *
 * Legacy ReasonList=All: overassigned count + inactive count (separate buckets).
 * Uses overassigned and inactive buckets separately to mirror legacy semantics.
 *
 * Percentage: legacy uses period-over-period (today vs 30 days ago).
 * Read from percentageChanges['Redundant_All'] which the pipeline computes from
 * the time series day-over-day comparison.
 */
export function toLicensesRedundant(
  s: ResourceSummaryDto | undefined,
  reasonList?: string[],
  vendor?: string,
): LicensesRedundantResponse | undefined {
  if (!s) return undefined;

  const resolveRedundantPercentage = (): number => {
    const vendorPctKey = vendor && vendor !== 'All' ? `Redundant_${vendor}` : 'Redundant_All';
    return Number(
      s.percentageChanges['Overassigned'] ??
      s.percentageChanges[vendorPctKey] ??
      s.percentageChanges['Redundant_All'] ??
      0,
    );
  };

  const hasAll = !reasonList?.length || reasonList.includes('All');
  if (hasAll) {
    const vendorKey = !vendor || vendor === 'All' ? 'All' : vendor;
    const preByVendor = getPrecomputedShape(s, 'redundantByVendor', vendorKey) as Record<string, unknown> | undefined;
    if (preByVendor) {
      return {
        overassigned: Number(preByVendor['overassigned'] ?? 0),
        percentage: resolveRedundantPercentage(),
      };
    }
  }

  if (reasonList?.length) {
    const overassigned = reasonList.reduce((acc, reason) => {
      const preByReason = getPrecomputedShape(s, 'redundantByReason', reason) as Record<string, unknown> | undefined;
      if (preByReason) {
        return acc + Number(preByReason['overassigned'] ?? 0);
      }
      return acc;
    }, 0);

    if (overassigned > 0) {
      const percentage = Number(
        (getPrecomputedShape(s, 'redundantByReason', reasonList[0]) as Record<string, unknown> | undefined)?.['percentage'] ??
            s.percentageChanges['Overassigned'] ??
            s.percentageChanges['Redundant_All'] ??
          0,
      );
      return { overassigned, percentage };
    }
  }

  const isAllVendors = !vendor || vendor === 'All';

  // Read from nested Aggregates structures
  const redundantDict = isAllVendors
    ? getNestedDict(s, 'Redundant')
    : (getNestedDict(s, 'VendorRedundant')[vendor] as Record<string, unknown>) ?? {};

  const inactive = Number(redundantDict['Inactive'] ?? 0);
    const overassignedOnly = Number(redundantDict['Overassigned'] ?? 0);
  const all = Number(redundantDict['All'] ?? Number.NaN);
  const overassigned = hasAll
    ? all
    : reasonList.reduce((acc, reason) => {
      if (reason === 'Inactive') return acc + inactive;
      if (reason === 'Overassigned') return acc + overassignedOnly;
      return acc;
    }, 0);

  if (hasAll && !Number.isFinite(overassigned)) return undefined;

  // Prefer the overassigned % key first because it most closely matches
  // legacy snapshot semantics for the top-level Utilisation tile.
  const percentage = resolveRedundantPercentage();

  return {
    overassigned,
    percentage,
  };
}

/**
 * Expired subscriptions snapshot.
 * Reads the pipeline's `Expired` key which sums TotalLicenseCount for
 * subscriptions with Status "Expired" or "Disabled"  matching the legacy
 * SubscriptionRepository.GetExpiredNumberSnapshotAsync exactly.
 * Percentage is period-over-period from the time series (day-over-day).
 */
export function toLicensesExpired(
  s: ResourceSummaryDto | undefined
): LicensesExpiredResponse | undefined {
  if (!s) return undefined;

  const pre = getPrecomputedShape(s, 'expired') as Record<string, unknown> | undefined;
  const preExpired = Number(pre?.['expiredSubscriptions'] ?? Number.NaN);
  if (pre && Number.isFinite(preExpired)) {
    return {
      expiredSubscriptions: preExpired,
      percentage: Number(pre['percentage'] ?? 0),
    };
  }

  const expired = getNestedDict(s, 'Expired');
  const total = Number(expired['Total'] ?? Number.NaN);
  if (!Number.isFinite(total)) return undefined;

  return {
    expiredSubscriptions: total,
    percentage: Number(s.percentageChanges['Expired'] ?? 0),
  };
}

/**
 * Hardware inventory snapshot.
 * typeList filters client-side from Type_* CountsByFilter entries.
 */
export function toHardwareInventorySnapshot(
  s: ResourceSummaryDto | undefined,
  typeList?: string[]
): InventorySnapshotResponse | undefined {
if (!s) return undefined;
const inventorySnapshot: Record<string, number> = {};
const typeDict = getNestedDict(s, 'Type');
// Sort keys alphabetically for consistent ordering across platforms
const sortedKeys = Object.keys(typeDict).sort();
for (const type of sortedKeys) {
  if (!typeList?.length || typeList.includes(type)) {
      inventorySnapshot[type] = Number(typeDict[type] ?? 0);
    }
  }
  return { inventorySnapshot };
}

// ?? Security page adapters ???????????????????????????????????????????

/**
 * MFA status snapshot.
 * userType filters client-side: 'Member' ? Type_Member, 'Guest' ? Type_External.
 * With no filter, sums all MFA_* keys.
 */
export function toMfaStatusSnapshot(
  s: ResourceSummaryDto | undefined
): MfaStatusSnapshotResponse | undefined {
  if (!s) return undefined;
  const mfa = getNestedDict(s, 'MFA');
  return {
    usersWithoutMfa: Number(mfa['WithoutMfa'] ?? mfa['WithoutMFA'] ?? 0),
    usersWithMfa: Number(mfa['WithMfa'] ?? mfa['WithMFA'] ?? 0),
  };
}

export function toSharedMailboxesSnapshot(
  s: ResourceSummaryDto | undefined
): UserPercentageResponse | undefined {
  if (!s) return undefined;

  return {
    users: Number(s.countsByFilter['Enabled'] ?? 0),
    percentage: s.percentageChanges['TotalCount'] ?? 0,
  };
}

export function toNoLicencesSnapshot(
  s: ResourceSummaryDto | undefined
): UserPercentageResponse | undefined {
  if (!s) return undefined;

  return {
    users: Number(s.countsByFilter['WithoutLicenses'] ?? 0),
    percentage: s.percentageChanges['TotalCount'] ?? 0,
  };
}

export function toSecureScoreSnapshot(
  s: ResourceSummaryDto | undefined
): MsSecureScoreSnapshotResponse | undefined {
  if (!s) return undefined;
  return {
    value: Number(s.aggregates['CurrentScore'] ?? s.aggregates['CurrentScoreOverall'] ?? 0),
    percentage: s.percentageChanges['Score'] ?? 0,
  };
}

// ?? Adoption page adapters ???????????????????????????????????????????

export function toServiceAdoptionSnapshot(
  s: ResourceSummaryDto | undefined,
  serviceType: string = 'allservices',
  days: string = '30'
): ServiceAdoptionSnapshotResponse | undefined {
  if (!s) return undefined;
  return toCanonicalServiceAdoptionSnapshot(s, serviceType, days);
}

export function toTeamsGroupsAdoptionSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30'
): TeamsGroupsAdoptionSnapshotResponse | undefined {
  if (!s) return undefined;

  const inTeamByWindow = getNestedDict(s, 'InTeamByWindow');
  const windowBucket = getCaseInsensitive(inTeamByWindow, days) as Record<string, unknown> | undefined;
  if (!windowBucket) return undefined;

  const inTeam = Number(windowBucket['InTeam'] ?? Number.NaN);
  const notInTeam = Number(windowBucket['NotInTeam'] ?? Number.NaN);
  if (!Number.isFinite(inTeam) || !Number.isFinite(notInTeam)) return undefined;

  return {
    inTeam,
    notInTeam,
  };
}

export function toCollabSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30'
): CollabSnapshotResponse | undefined {
  if (!s) return undefined;

  const collabByWindow = getNestedDict(s, 'CollaborationByWindow');
  const windowBucket = getCaseInsensitive(collabByWindow, days) as Record<string, unknown> | undefined;
  if (!windowBucket) return undefined;

  const collaborate = Number(windowBucket['Collaborating'] ?? Number.NaN);
  const notCollaborate = Number(windowBucket['NotCollaborating'] ?? Number.NaN);
  if (!Number.isFinite(collaborate) || !Number.isFinite(notCollaborate)) return undefined;

  return {
    collaborate,
    notCollaborate,
  };
}

// ?? Productivity page adapters ???????????????????????????????????????

export function toCallsMadeSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30'
): NumberSnapshotResponse | undefined {
  if (!s) return undefined;
  const byDays = getNestedDict(s, 'CallsMadeByDays');
  const pctByDays = getNestedDict(s, 'CallsMadePctByDays');
  const value = getWindowedShapeNumber(Object.keys(byDays).length ? byDays : undefined, days);
  if (value === undefined || !Number.isFinite(value)) return undefined;
  const percentage = getWindowedShapeNumber(Object.keys(pctByDays).length ? pctByDays : undefined, days) ?? 0;
  return { value, percentage };
}

export function toEmailsSentSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30'
): NumberSnapshotResponse | undefined {
  if (!s) return undefined;
  const byDays = getNestedDict(s, 'EmailsSentByDays');
  const pctByDays = getNestedDict(s, 'EmailsSentPctByDays');
  const value = getWindowedShapeNumber(Object.keys(byDays).length ? byDays : undefined, days);
  if (value === undefined || !Number.isFinite(value)) return undefined;
  const percentage = getWindowedShapeNumber(Object.keys(pctByDays).length ? pctByDays : undefined, days) ?? 0;
  return { value, percentage };
}

export function toMessagesSentSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30'
): NumberSnapshotResponse | undefined {
  if (!s) return undefined;
  const byDays = getNestedDict(s, 'MessagesSentByDays');
  const pctByDays = getNestedDict(s, 'MessagesSentPctByDays');
  const value = getWindowedShapeNumber(Object.keys(byDays).length ? byDays : undefined, days);
  if (value === undefined || !Number.isFinite(value)) return undefined;
  const percentage = getWindowedShapeNumber(Object.keys(pctByDays).length ? pctByDays : undefined, days) ?? 0;
  return { value, percentage };
}

export function toMeetingsSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30'
): NumberSnapshotResponse | undefined {
  if (!s) return undefined;
  const byDays = getNestedDict(s, 'MeetingsJoinedByDays');
  const pctByDays = getNestedDict(s, 'MeetingsJoinedPctByDays');
  const value = getWindowedShapeNumber(Object.keys(byDays).length ? byDays : undefined, days);
  if (value === undefined || !Number.isFinite(value)) return undefined;
  const percentage = getWindowedShapeNumber(Object.keys(pctByDays).length ? pctByDays : undefined, days) ?? 0;
  return { value, percentage };
}

// ?? Trend adapters (time series ? existing response shapes) 

export function toLicensesTrend(
  s: ResourceSummaryDto | undefined,
  days: string = '30',
): LicensesTrendResponse | undefined {
  const trend = toWindowedTrendFromSeriesKeys(s, ['TotalCount'], days);
  if (!trend?.length) return undefined;
  return {
    trend: trend.map((p) => ({ date: p.date, value: Number(p.value ?? Number.NaN) })),
    compareAgainstTrend: [],
  };
}

export function toLicensesUnassignedTrend(
  s: ResourceSummaryDto | undefined,
  days: string = '30',
): LicensesTrendResponse | undefined {
  const dayWindow = Math.max(1, Number(days) || 30);
  const inventory = toLicenseInventoryTrend(s, dayWindow);
  if (!inventory?.inactiveTrend?.length) return undefined;

  return {
    trend: inventory.inactiveTrend.map((p) => ({
      date: p.date,
      value: Number.isFinite(p.value as number) ? Number(p.value) : Number.NaN,
    })),
    compareAgainstTrend: [],
  };
}

export function toLicensesOverassignedTrend(
  s: ResourceSummaryDto | undefined,
  days: string = '30',
): LicensesTrendResponse | undefined {
  if (!s) return undefined;

  // Prefer precomputed redundant trend if pipeline emitted it.
  const pre = getPrecomputedShape(s, 'redundantTrend') as Record<string, unknown> | undefined;
  if (pre && Array.isArray(pre['trend'])) {
    const raw = pre['trend'] as Array<Record<string, unknown>>;
    return {
      trend: raw.map((p) => ({ date: String(p['date'] ?? ''), value: Number(p['value'] ?? Number.NaN) })),
      compareAgainstTrend: [],
    };
  }

  // No nested/precomputed trend available.
  return undefined;
}

export function toLicensesInactiveTrend(
  s: ResourceSummaryDto | undefined,
  days: string = '30',
): LicensesTrendResponse | undefined {
  const dayWindow = Math.max(1, Number(days) || 30);
  const preferredSeries = [`Activity_InactiveLast${dayWindow}Days`];
  const trend = toWindowedTrendFromSeriesKeys(s, preferredSeries, days);

  if (!trend?.length) return undefined;
  return {
    trend: trend.map((p) => ({ date: p.date, value: Number(p.value ?? Number.NaN) })),
    compareAgainstTrend: [],
  };
}

export function toLicensesExpiredBarChart(
  s: ResourceSummaryDto | undefined
): LicencesExpiredBarChartResponse | undefined {
  if (!s) return undefined;
  const expiredByServiceType = getNestedDict(s, 'ExpiredByServiceType');

  return {
    barChartSnapshot: Object.fromEntries(
      Object.entries(expiredByServiceType).map(([key, value]) => [key, Number(value ?? 0)]),
    ),
  };
}

/**
 * Service usage trend over time from UserActivity time series.
 * serviceType maps to a time series key. days determines the window.
 * Returns current window + compare-against (previous window of same length).
 */
export function toServiceUsageTrend(
  s: ResourceSummaryDto | undefined,
  serviceType: string = 'messaging',
  days: string = '7'
): LicensesUsageTrendResponse | undefined {
  const normalizedType = serviceType.toLowerCase();
  const supportedTypes = new Set(['messaging', 'email', 'videocalls', 'phonecalls', 'filestorage']);
  if (!supportedTypes.has(normalizedType)) return undefined;

  const hierarchyMetricByType: Record<string, string> = {
    messaging: 'MessagesSent',
    email: 'EmailsSent',
    videocalls: 'VideoCallsDuration',
    phonecalls: 'PhoneCallsDuration',
    filestorage: 'FilesViewedOrModified',
  };

  const flatKeysByType: Record<string, string[]> = {
    messaging: ['LicenseFiltered_MessagesSent', 'MessagesSent'],
    email: ['LicenseFiltered_EmailsSent', 'EmailsSent'],
    videocalls: ['LicenseFiltered_VideoCallsDuration', 'VideoCallsDuration'],
    phonecalls: ['LicenseFiltered_PhoneCallsDuration', 'PhoneCallsDuration'],
    filestorage: ['LicenseFiltered_FilesViewedOrModified', 'FilesViewedOrModified'],
  };

  const hierarchyMetric = hierarchyMetricByType[normalizedType];
  const flatLicenseFilteredKey = flatKeysByType[normalizedType]?.[0];

  // If flat timeSeries key is absent but hierarchy has the series, inject it so the windowed helper can use it
  let sResolved = s;
  const hierarchySeries = getHierarchySeries(s, 'LicenseFilteredActivity', hierarchyMetric);
  if (hierarchySeries?.length && flatLicenseFilteredKey && !s?.timeSeries?.[flatLicenseFilteredKey]?.length) {
    sResolved = { ...s!, timeSeries: { ...s!.timeSeries, [flatLicenseFilteredKey]: hierarchySeries } };
  }

  const seriesKeys = flatKeysByType[normalizedType] ?? [];
  const windowDays = Math.max(1, Number(days) || 7);
  const trend = toWindowedTrendFromSeriesKeys(sResolved, seriesKeys, String(windowDays));
  const compareAgainst = toWindowedTrendFromSeriesKeys(sResolved, seriesKeys, String(windowDays), windowDays);
  if (!trend || !compareAgainst) return undefined;

  const toUsageTrend = (points: TrendDataItem[]) =>
    points.map((point) => ({
      date: point.date,
      vendor: 'Microsoft',
      value: Number(point.value ?? Number.NaN),
    }));

  return {
    trend: toUsageTrend(trend),
    compareAgainst: toUsageTrend(compareAgainst),
  };
}

/**
 * Activity per day trend from time series.
 * activityType selects the series key: allactivities sums all, otherwise uses the specific series.
 */
export function toActivityPerDayTrend(
  s: ResourceSummaryDto | undefined,
  activityType: string = 'allactivities',
  days: string = '30',
): ActivityPerDayTrendResponse | undefined {
  const trend = toWindowedTrendFromSeriesKeys(s, getActivitySeriesKeys(activityType), days);
  if (trend) return { trend };

  const pre = getPrecomputedShape(s, 'activityTrendByType', activityType) as Record<string, unknown> | undefined;
  if (!pre || !Array.isArray(pre['trend'])) return undefined;
  return {
    trend: pre['trend'].map((x) => ({
      date: String((x as Record<string, unknown>)['date'] ?? ''),
      value: Number((x as Record<string, unknown>)['value'] ?? 0),
    })),
  };
}

export function toSecureScoreTrend(
  s: ResourceSummaryDto | undefined
): TrendDataItem[] | undefined {
  return extractTrend(s, 'Score');
}

//  Licences expiring soon (from Subscriptions summary) 

/**
 * Expiring licences buckets: 7, 30, 60, 90 day windows.
 * Maps to the LicensesExpiringResponse shape { expirations: [{count, days}] }.
 */
export function toLicensesExpiring(
  s: ResourceSummaryDto | undefined
): LicensesExpiringResponse | undefined {
  if (!s) return undefined;

  const pre = getPrecomputedShape(s, 'expiring') as Record<string, unknown> | undefined;
  if (pre && Array.isArray(pre['expirations'])) {
    const expirations = (pre['expirations'] as Array<Record<string, unknown>>).map((x) => ({
      days: Number(x['days'] ?? 0),
      count: Number(x['count'] ?? 0),
    }));
    return { expirations } as LicensesExpiringResponse;
  }

  const expiring = getNestedDict(s, 'Expiring');
  const expirations = [7, 30, 60, 90]
    .map((days) => ({
      days,
      count: Number(expiring[`Next${days}Days`] ?? Number.NaN),
    }))
    .filter((x) => Number.isFinite(x.count));

  return expirations.length ? { expirations } as LicensesExpiringResponse : undefined;
}

/**
 * Expiring licences vendor split within the next 90 days.
 * Maps to the LicensesExpiringVendorResponse shape { vendorSnapshot: { ... } }.
 */
export function toLicensesExpiringVendor(
  s: ResourceSummaryDto | undefined
): LicensesExpiringVendorResponse | undefined {
  if (!s) return undefined;
  const vendorDict = getNestedDict(s, 'ExpiringByVendor');

  const vendorSnapshot = Object.fromEntries(
    Object.entries(vendorDict).map(([vendor, value]) => {
      if (typeof value === 'number') return [vendor, Number(value)];
      if (value && typeof value === 'object') {
        const bucket = value as Record<string, unknown>;
        return [vendor, Number(bucket['Next90Days'] ?? bucket['Total'] ?? 0)];
      }
      return [vendor, 0];
    }),
  );

  return {
    vendorSnapshot,
  } as LicensesExpiringVendorResponse;
}

/**
 * SharePoint usage snapshot. dataType selects which metric to show.
 */
export function toSharePointUsageSnapshot(
  s: ResourceSummaryDto | undefined,
  dataType: string = 'sites',
  days: string = '30',
  sitesSummary?: ResourceSummaryDto
): NumberSnapshotResponse | undefined {
  if (dataType === 'sites') {
    if (!sitesSummary) return undefined;
    return {
      value: Number(sitesSummary.countsByFilter['NonPersonalSites'] ?? sitesSummary.totalCount ?? 0),
      percentage: Number(
        sitesSummary.percentageChanges['NonPersonalSites'] ??
          sitesSummary.percentageChanges['TotalCount'] ??
          0,
      ),
    };
  }

  if (!s) return undefined;

  const hierarchyKeyByType: Record<string, string> = {
    activesites: 'ActiveSites',
    pages: 'Pages',
    visits: 'Visits',
    files: 'Files',
  };

  const normalizedType = dataType.toLowerCase();
  const hierarchyMetric = hierarchyKeyByType[normalizedType];

  if (hierarchyMetric) {
    const spWindows = getHierarchy(s, 'SharePointWindows');
    const windowBucket = getCaseInsensitive(spWindows, days) as Record<string, unknown> | undefined;
    const metricBucket = windowBucket ? getCaseInsensitive(windowBucket, hierarchyMetric) as Record<string, unknown> | undefined : undefined;
    if (metricBucket) {
      return {
        value: Number(metricBucket['Current'] ?? metricBucket['current'] ?? 0),
        percentage: Number(metricBucket['PercentageChange'] ?? metricBucket['percentageChange'] ?? 0),
      };
    }
  }

  return undefined;
}

//  Device usage (from Devices summary) 

type DeviceUsageWindowShape = {
  Offline: number;
  OnlineUsed: number;
  OnlineUnused: number;
  OfflinePercentage: number;
};

type DeviceUsageShape = {
  All: Record<string, DeviceUsageWindowShape>;
  MeetingRooms: Record<string, DeviceUsageWindowShape>;
};

type MeetingRoomsBookedItemShape = {
  id: string;
  name: string;
  bookedMinutes: number;
  percentage: number;
};

type MeetingRoomsBookedShape = {
  top: MeetingRoomsBookedItemShape[];
  bottom: MeetingRoomsBookedItemShape[];
};

function getDeviceUsageShape(s: ResourceSummaryDto | undefined): DeviceUsageShape | undefined {
  if (!s) return undefined;
  const preferred = getNestedDict(s, 'DeviceUsage');
  const raw = preferred;
  if (raw && typeof raw === 'object' && Object.keys(raw as Record<string, unknown>).length > 0) {
    return raw as DeviceUsageShape;
  }
  return undefined;
}

function getDeviceUsageWindow(
  shape: DeviceUsageShape | undefined,
  scope: 'All' | 'MeetingRooms',
  days: string,
): DeviceUsageWindowShape | undefined {
  if (!shape) return undefined;
  const scopeValue = getCaseInsensitive(shape as unknown as Record<string, unknown>, scope) as Record<string, unknown> | undefined;
  if (!scopeValue) return undefined;
  return getCaseInsensitive(scopeValue, days) as DeviceUsageWindowShape | undefined;
}

function mapUsageBucket(bucket: DeviceUsageWindowShape | Record<string, unknown> | undefined): DeviceUsageSnapshotResponse | undefined {
  if (!bucket || typeof bucket !== 'object') return undefined;
  const record = bucket as Record<string, unknown>;

  const offline = Number(getCaseInsensitive(record, 'Offline') ?? 0);
  const onlineUnused = Number(getCaseInsensitive(record, 'OnlineUnused') ?? getCaseInsensitive(record, 'Unused') ?? 0);
  const onlineUsed = Number(
    getCaseInsensitive(record, 'OnlineUsed') ??
    getCaseInsensitive(record, 'Used') ??
    getCaseInsensitive(record, 'Online') ??
    0,
  );

  return {
    offlineDevicesCount: offline,
    onlineButNotUsedDevicesCount: onlineUnused,
    onlineDevicesCount: onlineUsed,
  };
}

function getMeetingRoomsBookedShape(s: ResourceSummaryDto | undefined): MeetingRoomsBookedShape | undefined {
  if (!s) return undefined;
  const raw = getNestedDict(s, 'MeetingRoomsBookedShape') as unknown;
  if (!raw || typeof raw !== 'object' || Object.keys(raw as Record<string, unknown>).length === 0) return undefined;
  return raw as MeetingRoomsBookedShape;
}

/**
 * Device usage snapshot: offline / online-unused / online-used for a given day window.
 */
export function toDeviceUsageSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30',
  _deviceType?: string
): DeviceUsageSnapshotResponse | undefined {
  const shape = getDeviceUsageShape(s);
  const bucket = getDeviceUsageWindow(shape, 'All', days);
  return mapUsageBucket(bucket);
}

//  Meeting room device usage (reuses Device usage shape) 

/**
 * Meeting room device usage from separate MeetingRoomUsage_* keys.
 * These are computed only from devices with AsigneeType = "Meeting Room".
 */
export function toMeetingRoomDeviceUsageSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30',
  _deviceType?: string
): MeetingRoomDeviceUsageSnapshotResponse | undefined {
  const shape = getDeviceUsageShape(s);
  const bucket = getDeviceUsageWindow(shape, 'MeetingRooms', days);
  return mapUsageBucket(bucket);
}

//  Groups & Teams inventory (from UserAssociatedTeams summary) 

/**
 * Total teams count + percentage change.
 */
export function toGroupsTeamsInventorySnapshot(
  s: ResourceSummaryDto | undefined
): NumberSnapshotResponse | undefined {
  if (!s) return undefined;

  const totalFromFilter = s.countsByFilter['TotalCount'];
  const totalTeams = s.countsByFilter['TotalTeams'];
  const totalFromSummary = Number.isFinite(s.totalCount) && s.totalCount > 0
    ? s.totalCount
    : undefined;
  const total = totalFromFilter ?? totalTeams ?? totalFromSummary;

  if (total === undefined) return undefined;

  return {
    value: Number(total),
    percentage: s.percentageChanges['TotalCount'] ?? 0,
  };
}

//  External Teams access (from UserAssociatedTeams summary) 

/**
 * Count of external users.
 * Legacy counts all external users regardless of team membership.
 */
export function toExternalTeamsAccessSnapshot(
  s: ResourceSummaryDto | undefined
): ExternalTeamsAccessResponse | undefined {
  if (!s) return undefined;
  const externalUsers = s.countsByFilter['ExternalUsers'];
  if (externalUsers === undefined) return undefined;

  return {
    value: Number(externalUsers),
  };
}

export function toSuspiciousActivitySnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '7'
): SuspiciousActivityTotalEventResponse | undefined {
  if (!s) return undefined;
  const dayValue = Math.max(1, Number(days) || 7);
  const key = `SuspiciousLast${dayValue}Days`;
  return {
    count: s.countsByFilter[key] ?? 0,
    percentage: s.percentageChanges['TotalCount'] ?? 0,
  };
}

export function toSuspiciousActivitySourceCountrySnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '7'
): SuspiciousActivitySourceCountryResponse | undefined {
  if (!s) return undefined;
  const dayValue = Math.max(1, Number(days) || 7);
  const key = `SourceCountrySnapshotLast${dayValue}Days`;
  const snapshot = getNestedDict(s, key);
  return {
    snapshot: Object.fromEntries(
      Object.entries(snapshot).map(([country, count]) => [country, Number(count ?? 0)]),
    ),
  };
}

export function toSuspiciousActivityTrend(
  s: ResourceSummaryDto | undefined,
  days: string = '7',
  eventType?: string,
): { trend: TrendDataItem[] } | undefined {
  if (!s) return undefined;

  const byType = getNestedDict(s, 'DailyCountsByEventType');
  if (Object.keys(byType).length === 0) {
    // No DailyCountsByEventType aggregate means no events in history.
    // Return empty trend (valid response with zero data points) instead of undefined.
    return { trend: [] };
  }

  const selectedType = isAllSelection(eventType) ? 'All' : (eventType ?? 'All');
  const selected = getCaseInsensitive(byType, selectedType) as Record<string, unknown> | undefined;
  if (selected && typeof selected === 'object') {
    const dayCount = Number(days) || 7;
    const points = Object.entries(selected)
      .map(([rawDate, value]) => {
        const parsed = new Date(rawDate);
        if (Number.isNaN(parsed.getTime())) {
          return null;
        }

        return {
          timestamp: `${rawDate}T00:00:00Z`,
          value: Number(value ?? 0),
        };
      })
      .filter((point): point is TimeSeriesDataPointDto => point !== null)
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    if (!points.length) {
      // Selected event type has no data points; return empty trend.
      return { trend: [] };
    }

    const fallbackEndDate = points[points.length - 1].timestamp.slice(0, 10);
    const endDateKey = /^\d{4}-\d{2}-\d{2}$/.test(s.lastUpdatedAt?.slice(0, 10) ?? '')
      ? s.lastUpdatedAt.slice(0, 10)
      : fallbackEndDate;

    const end = new Date(`${endDateKey}T00:00:00Z`);
    const start = new Date(end);
    start.setUTCDate(start.getUTCDate() - (dayCount - 1));

    const trend = points
      .filter((point) => {
        const date = new Date(point.timestamp);
        return date >= start && date <= end;
      })
      .map((point) => ({
        date: point.timestamp,
        value: Number(point.value ?? 0),
      }));

    return { trend };
  }

  // Selected event type not found in aggregate; return empty trend.
  return { trend: [] };
}

export function toExternalApplicationsSnapshot(
  s: ResourceSummaryDto | undefined
): ExternalAppsMSDataResponse | undefined {
  if (!s) return undefined;
  return {
    totalCount: s.totalCount,
    percentage: s.percentageChanges['TotalCount'] ?? 0,
  };
}

export function toAccessIntrusivenessSnapshot(
  s: ResourceSummaryDto | undefined
): ExternalAppsAccountLevelResponse | undefined {
  if (!s) return undefined;

  const high = s.countsByFilter['High'];
  const medium = s.countsByFilter['Medium'];
  const low = s.countsByFilter['Low'];
  if (high === undefined || medium === undefined || low === undefined) return undefined;

  return {
    snapshot: {
      High: Number(high),
      Medium: Number(medium),
      Low: Number(low),
    },
  };
}

export function toAccessIntrusivenessTenantSnapshot(
  s: ResourceSummaryDto | undefined
): ExternalAppsTenantLevelResponse | undefined {
  if (!s) return undefined;

  const high = s.countsByFilter['High'];
  const medium = s.countsByFilter['Medium'];
  const low = s.countsByFilter['Low'];
  if (high === undefined || medium === undefined || low === undefined) return undefined;

  return {
    snapshot: {
      High: Number(high),
      Medium: Number(medium),
      Low: Number(low),
    },
  };
}

export function toPublisherVerificationSnapshot(
  s: ResourceSummaryDto | undefined
): ExternalAppsPublisherVerificationResponse | undefined {
  if (!s) return undefined;

  const unverified = s.countsByFilter['Unverified'];
  const verified = s.countsByFilter['Verified'];
  if (unverified === undefined || verified === undefined) return undefined;

  return {
    unverified: Number(unverified),
    verified: Number(verified),
  };
}

export function toExternalUsersAccessSnapshot(
  s: ResourceSummaryDto | undefined
): ExternalUsersAccessResponse | undefined {
  if (!s) return undefined;
    const teamsWithExternalAccess = s.countsByFilter['TeamsWithExternalAccess'];
    const teamsWithoutExternalAccess = s.countsByFilter['TeamsWithoutExternalAccess'];
    if (teamsWithExternalAccess === undefined || teamsWithoutExternalAccess === undefined) return undefined;

  return {
      teamsWithExternalAccess: Number(teamsWithExternalAccess),
      teamsWithoutExternalAccess: Number(teamsWithoutExternalAccess),
  };
}

export function toDailyMeetingAttendanceTrend(
  s: ResourceSummaryDto | undefined,
  days: string = '30',
): MeetingAdoptionTrendResponse | undefined {
  const trend = toWindowedTrendFromSeriesKeys(s, ['TotalMeetingsJoined', 'MeetingsJoined'], days);
  if (!trend?.length) return undefined;

  return {
    trend: trend.map((point) => ({
      date: point.date,
      value: Number(point.value ?? Number.NaN),
    })),
  };
}

export function toFailedLoginAccountsSnapshot(
  accountSecuritySummary: ResourceSummaryDto | undefined,
  loginTypeSummary?: ResourceSummaryDto,
  days: string = '30',
  mfaStatus: string = 'all',
): FailedLoginAccountsSnapshotResponse | undefined {
  if (!accountSecuritySummary) return undefined;

  const normalizedMfa = mfaStatus.trim().toLowerCase();
  const mfaKey = normalizedMfa === 'enabled'
    ? 'Enabled'
    : normalizedMfa === 'disabled'
      ? 'Disabled'
      : normalizedMfa === 'enforced'
        ? 'Enforced'
        : 'All';

  const failedByDays = getNestedDict(accountSecuritySummary, 'FailedLoginsByMfaAndDays');
  const failedDayBucket = getWindowedShapeBucket(failedByDays, days);

  const failedPctByDays = getNestedDict(accountSecuritySummary, 'FailedLoginsPercentageByMfaAndDays');
  const failedPctDayBucket = getWindowedShapeBucket(failedPctByDays, days);

  const successfulByDays = loginTypeSummary ? getNestedDict(loginTypeSummary, 'SuccessfulLoginsByDays') : undefined;

  const failedMsLogins = Number(
    getCaseInsensitive(failedDayBucket ?? {}, mfaKey) ?? 0,
  );
  const percentage = Number(
    getCaseInsensitive(failedPctDayBucket ?? {}, mfaKey) ?? 0,
  );
  const successfulMsLogins = Number(
    getWindowedShapeNumber(successfulByDays, days) ?? 0,
  );

  return {
    failedMsLogins,
    percentage,
    successfulMsLogins,
  };
}

export function toFailedLoginCountriesSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30'
): FailedLoginDataResponse | undefined {
  if (!s) return undefined;

  const byDays = getNestedDict(s, 'FailedLoginsByCountryByDays');
  const dayBucket = getWindowedShapeBucket(byDays, days);
  const aggregateBucket = getNestedDict(s, 'FailedLoginsByCountry');
  const sourceBucket = dayBucket ?? (Object.keys(aggregateBucket).length ? aggregateBucket : undefined);
  if (!sourceBucket) return undefined;

  if (Array.isArray(sourceBucket)) {
    return {
      snapshot: Object.fromEntries(
        sourceBucket.map((item) => {
          const row = item as Record<string, unknown>;
          const countryCode = String(row['countryCode'] ?? row['CountryCode'] ?? '');
          const count = String(row['count'] ?? row['Count'] ?? '0');
          return [toCountryDisplayName(countryCode), count];
        })
      ),
    };
  }

  return {
    snapshot: Object.fromEntries(
      Object.entries(sourceBucket).map(([key, value]) => [toCountryDisplayName(key), String(value ?? '0')])
    ),
  };
}

export function toFailedLoginTypesSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30'
): FailedLoginDataResponse | undefined {
  if (!s) return undefined;

  const byDays = getNestedDict(s, 'FailedLoginsByTypeByDays');
  const dayBucket = getWindowedShapeBucket(byDays, days);
  const aggregateBucket = getNestedDict(s, 'FailedLoginsByType');
  const sourceBucket = dayBucket ?? (Object.keys(aggregateBucket).length ? aggregateBucket : undefined);
  if (!sourceBucket) return undefined;

  if (Array.isArray(sourceBucket)) {
    return {
      snapshot: Object.fromEntries(
        sourceBucket.map((item) => {
          const row = item as Record<string, unknown>;
          const type = String(row['type'] ?? row['Type'] ?? '');
          const count = String(row['count'] ?? row['Count'] ?? '0');
          return [type, count];
        })
      ),
    };
  }

  return {
    snapshot: Object.fromEntries(
      Object.entries(sourceBucket).map(([key, value]) => [key, String(value ?? '0')])
    ),
  };
}

export function toMailboxForwardingRulesSnapshot(
  s: ResourceSummaryDto | undefined
): MailboxesForwardingSnapshotResponse | undefined {
  if (!s) return undefined;
  const enabled = s.countsByFilter['EnabledRules'];
  const disabled = s.countsByFilter['DisabledRules'];
  if (enabled === undefined || disabled === undefined) return undefined;
  return {
    snapshot: {
      Enabled: enabled,
      Disabled: disabled,
    },
  };
}

export function toOutdatedBrowsersFilterOptions(
  s: ResourceSummaryDto | undefined,
): string[] {
  if (!s) return [];

  const byAccountAndBrowser = getNestedDict(s, 'ActivityByAccountTypeAndBrowser');
  const allAccountBucket = getCaseInsensitive(byAccountAndBrowser, 'All') as Record<string, unknown> | undefined;
  if (!allAccountBucket) return [];

  return Object.keys(allAccountBucket).filter((browser) => browser.toLowerCase() !== 'all');
}

export function toOutdatedOperatingSystemsFilterOptions(
  s: ResourceSummaryDto | undefined,
  days: string = '30',
): string[] {
  if (!s) return [];

  const byDays = getNestedDict(s, 'ActivityByAccountTypeAndOsByDays');
  const dayBucket = getCaseInsensitive(byDays, days) as Record<string, unknown> | undefined;
  const byAccountAndOs = dayBucket ?? getNestedDict(s, 'ActivityByAccountTypeAndOs');
  const allAccountBucket = getCaseInsensitive(byAccountAndOs, 'All') as Record<string, unknown> | undefined;
  if (!allAccountBucket) return [];

  return Object.keys(allAccountBucket).filter((osType) => osType.toLowerCase() !== 'all');
}

export function toOutdatedBrowsersSnapshot(
  s: ResourceSummaryDto | undefined,
  accountType?: string,
  browserType?: string,
): OutdatedOSSActivitynapshotResponse | undefined {
  if (!s) return undefined;

  const byAccountAndBrowser = getNestedDict(s, 'ActivityByAccountTypeAndBrowser');
  const normalizedAccount = isAllSelection(accountType, ['allusers']) ? 'All' : (accountType ?? 'All');
  const normalizedBrowser = isAllSelection(browserType, ['allbrowsers']) ? 'All' : (browserType ?? 'All');
  const accountBucket = getCaseInsensitive(byAccountAndBrowser, normalizedAccount) as Record<string, unknown> | undefined;

  if (accountBucket) {
    const count = getCaseInsensitive(accountBucket, normalizedBrowser);
    if (count !== undefined) {
      return {
        totalCount: Number(count ?? 0),
      };
    }
  }

  return {
    totalCount: s.totalCount,
  };
}

export function toOutdatedOperatingSystemsSnapshot(
  s: ResourceSummaryDto | undefined,
  accountType?: string,
  osType?: string,
  days: string = '30',
): OutdatedOSSActivitynapshotResponse | undefined {
  if (!s) return undefined;

  const byDays = getNestedDict(s, 'ActivityByAccountTypeAndOsByDays');
  const dayBucket = getCaseInsensitive(byDays, days) as Record<string, unknown> | undefined;
  const byAccountAndOs = dayBucket ?? getNestedDict(s, 'ActivityByAccountTypeAndOs');
  const normalizedAccount = isAllSelection(accountType, ['allusers']) ? 'All' : (accountType ?? 'All');
  const normalizedOs = isAllSelection(osType, ['allos']) ? 'All' : (osType ?? 'All');
  const accountBucket = getCaseInsensitive(byAccountAndOs, normalizedAccount) as Record<string, unknown> | undefined;

  if (!accountBucket) return undefined;

  const count = getCaseInsensitive(accountBucket, normalizedOs);
  if (count === undefined) return undefined;

  return {
    totalCount: Number(count ?? 0),
  };
}

//  Microsoft Secure Score (from SecureScores summary) 

/**
 * Secure score snapshot matching the SecureScoreTypesSnapshotResponse shape.
 */
export function toSecureScoreTypesSnapshot(
  s: ResourceSummaryDto | undefined
): SecureScoreTypesSnapshotResponse | undefined {
  if (!s) return undefined;

  return {
    category: 'Overall',
    scorePercentage: Number(s.aggregates['CurrentScoreOverall'] ?? s.aggregates['CurrentScore'] ?? 0),
    changePercentage: Number(s.percentageChanges['ScoreOverall'] ?? s.percentageChanges['Score'] ?? 0),
  };
}

//  Licence inventory changes over time (from Subscriptions time series) 

/**
 * License inventory trend: total licences + inactive licences over time.
 *
 * Reads the canonical ExcludingFreeLicenseInventory hierarchy series for
 * TotalLicenses and InactiveLicenses.
 */
export function toLicenseInventoryTrend(
  s: ResourceSummaryDto | undefined,
  days: number = 90,
): { totalTrend: TrendDataItem[]; inactiveTrend: TrendDataItem[] } | undefined {
  // ExcludingFree series filters out free subscriptions (>= 5000 seats, e.g. Windows Store)
  // and matches the chart contract used by the pipeline.
  const totalRaw = getHierarchySeries(s, 'ExcludingFreeLicenseInventory', 'TotalLicenses') ?? [];
  const totalNormalized = normalizeDailySeriesWithGaps(totalRaw);
  if (!totalNormalized?.length) return undefined;

  // Anchor window to the most recent data date if it's before today.
  // This ensures historical data is shown even when the pipeline hasn't run today.
  const todayKey = new Date().toISOString().slice(0, 10);
  const lastDataKey = totalNormalized[totalNormalized.length - 1]?.timestamp?.slice(0, 10);
  const endAnchor = lastDataKey && lastDataKey < todayKey ? lastDataKey : undefined;

  const totalPoints = extendSeriesToTrailingWindow(totalNormalized, days, endAnchor);

  const inactiveRaw = getHierarchySeries(s, 'ExcludingFreeLicenseInventory', 'InactiveLicenses') ?? [];
  const inactiveNormalized = normalizeDailySeriesWithGaps(inactiveRaw);
  if (!inactiveNormalized?.length) return undefined;
  const inactivePoints = extendSeriesToTrailingWindow(inactiveNormalized, days, endAnchor);

  const totalTrend = totalPoints.map((p) => ({
    date: p.timestamp,
    value: p.value,
  }));
  const inactiveTrend = inactivePoints.map((p) => ({
    date: p.timestamp,
    value: p.value,
  }));

  return { totalTrend, inactiveTrend };
}

export function toLicenseInventoryChangesTrend(
  s: ResourceSummaryDto | undefined,
  days: number = 30,
): InventoryChangesTrendResponse | undefined {
  const trend = toLicenseInventoryTrend(s, days);
  if (!trend) return undefined;

  return {
    licenseInventory: trend.totalTrend.map((point, index) => ({
      date: point.date,
      totalLicenses: Number.isFinite(Number(point.value)) ? Number(point.value) : null,
      inactiveLicenses: Number.isFinite(Number(trend.inactiveTrend[index]?.value))
        ? Number(trend.inactiveTrend[index]?.value)
        : null,
    })),
  };
}

export function toMfaStatusSnapshotFiltered(
  s: ResourceSummaryDto | undefined,
  userType: string = 'All'
): MfaStatusSnapshotResponse | undefined {
  if (!s) return undefined;
  const xdim = getNestedDict(s, 'LicensedMfaCrossDimension');
  const bucket = userType === 'All' || userType === ''
    ? (xdim['All'] as Record<string, unknown>) ?? {}
    : (xdim[userType] as Record<string, unknown>) ?? {};
  // Property order must match MfaStatusSnapshotResponse: usersWithoutMfa first, usersWithMfa second.
  // The Security page renders Object.values(mfaStatusData) and labels with ['Users without MFA', 'Users with MFA'].
  return {
    usersWithoutMfa: Number(bucket['WithoutMfa'] ?? 0),
    usersWithMfa: Number(bucket['WithMfa'] ?? 0),
  };
}

//  MS Teams health with owner/membership filter (from TeamHealth summary) 

/**
 * MS Teams health from TeamHealth summary with dataType dimension.
 */
export function toMsTeamsHealthSnapshotFiltered(
  s: ResourceSummaryDto | undefined,
  dataType: string = 'membership'
): MsTeamsHealthSnapshotResponse | undefined {
  if (!s) return undefined;

  const key = dataType.toLowerCase() === 'owner' ? 'Ownership' : 'Membership';
  const bucket = getNestedDict(s, key);
  return {
    teamsWith: Number(bucket['With'] ?? 0),
    teamsWithout: Number(bucket['Without'] ?? 0),
  };
}

// ?? Filter option helpers (populate dropdowns from summary) ??????????

/**
 * Extracts vendor names from simple Vendor_* CountsByFilter keys.
 * Only matches keys like "Vendor_Microsoft" (no further underscores),
 * ignoring compound keys like "Vendor_Microsoft_UnderUtilized".
 */
export function toVendorList(s: ResourceSummaryDto | undefined): string[] {
  if (!s) return [];
  const vendorDict = getNestedDict(s, 'Vendor');
  return Object.keys(vendorDict);
}

/**
 * Extracts redundancy reason names from Reason_* CountsByFilter keys.
 * Used to populate the redundant licences reason dropdown.
 */
export function toReasonList(s: ResourceSummaryDto | undefined): string[] {
  if (!s) return [];
  const reasonDict = getNestedDict(s, 'Reason');
  const reasons = Object.keys(reasonDict).filter((reason) => reason.toLowerCase() !== 'unknown');
  // Always include Overassigned if present in CountsByFilter (from [Percentage] attribute)
  if (s.countsByFilter['Overassigned'] > 0 && !reasons.includes('Overassigned')) reasons.push('Overassigned');
  return reasons.length ? reasons : ['Overassigned', 'Inactive'];
}

/**
 * Extracts device type names for the hardware inventory filter.
 * Uses canonical Aggregates.AllTypes list.
 */
export function toDeviceTypeList(s: ResourceSummaryDto | undefined): string[] {
  if (!s) return [];
  const allTypes = getNestedArray(s, 'AllTypes');
  if (Array.isArray(allTypes)) {
    return allTypes
      .filter((t): t is string => typeof t === 'string' && t.trim().length > 0)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  return Object.keys(getNestedDict(s, 'Type'))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

//  Adoption self-contained tile adapters 

/**
 * User service adoption snapshot (per-service: messages, emails, phone, videocalls).
 * Uses UserActivity summary. The legacy API uses AND logic for "allservices".
 */
/**
 * User service adoption snapshot (per-service: messages, emails, phone, videocalls).
 * Uses UserActivity summary.
 *
 * Legacy "allservices" uses AND logic: user must use ALL four services.
 * Pipeline pre-computes FullAdoptionRate for this case.
 */
export function toUserServiceAdoptionSnapshot(
  s: ResourceSummaryDto | undefined,
  dataType: string = 'messages',
  days: string = '7'
): UserServiceAdoptionSnapshotResponse | undefined {
  if (!s) return undefined;
  return toCanonicalServiceAdoptionSnapshot(s, dataType, days);
}

/**
 * User service adoption trend for dashboard tile.
 * Mirrors legacy API semantics: exact trailing day window and null values for missing days.
 */
export function toUserServiceAdoptionTrend(
  s: ResourceSummaryDto | undefined,
  dataType: string = 'messages',
  days: string = '30',
): UserServiceAdoptionTrendResponse | undefined {
  const trend = toWindowedTrendFromSeriesKeys(s, getUserServiceAdoptionSeriesKeys(dataType), days);
  if (trend) return { trend };

  const pre = getPrecomputedShape(s, 'userServiceAdoptionTrendByType', dataType) as Record<string, unknown> | undefined;
  if (!pre || !Array.isArray(pre['trend'])) return undefined;

  return {
    trend: pre['trend'].map((x) => ({
      date: String((x as Record<string, unknown>)['date'] ?? ''),
      value: Number((x as Record<string, unknown>)['value'] ?? 0),
    })),
  };
}

/**
 * Device adoption snapshot - inactive device count.
 * Reads DeviceAdoptionStatus which uses the Status field, matching legacy
 * GetStatusSnapshotAsync (Status = administrative state, not network ConnectionStatus).
 */
export function toDeviceAdoptionSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30',
  deviceType: string = 'all'
): DeviceAdoptionSnapshotResponse | undefined {
  if (!s) return undefined;
  const bucketKey = deviceType === 'meetingrooms' ? 'MeetingRooms' : deviceType === 'users' ? 'Users' : 'All';

  const windowed = s.aggregates?.['WindowedDeviceAdoption'] as Record<string, Record<string, Record<string, number>>> | undefined;
  const windowBucket = windowed?.[days]?.[bucketKey];
  if (!windowBucket) return undefined;
  return {
    inactiveDevicesCount: windowBucket['Offline'] ?? 0,
    percentage: windowBucket['Percentage'] ?? 0,
  };
}

/**
 * Meeting adoption snapshot  users who joined/organized meetings vs those who haven't.
 * Uses UserActivity summary which has UsersWithMeetingActivity / UsersWithoutMeetingActivity.
 */
export function toMeetingAdoptionSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30'
): MeetingAdoptionSnapshotResponse | undefined {
  if (!s) return undefined;

  const meetingByWindow = getNestedDict(s, 'UserMeetingActivityByWindow');
  const windowBucket = getCaseInsensitive(meetingByWindow, days) as Record<string, unknown> | undefined;
  if (!windowBucket) return undefined;

  const setupJoinedMeeting = Number(windowBucket['WithActivity'] ?? Number.NaN);
  const hasntSetupJoinedMeeting = Number(windowBucket['WithoutActivity'] ?? Number.NaN);
  if (!Number.isFinite(setupJoinedMeeting) || !Number.isFinite(hasntSetupJoinedMeeting)) return undefined;

  return {
    setupJoinedMeeting,
    hasntSetupJoinedMeeting,
  };
}

/**
 * Inactive rooms snapshot  booked vs unbooked rooms.
 * Uses RoomUsage summary.
 */
export function toInactiveRoomsSnapshot(
  s: ResourceSummaryDto | undefined,
  roomsSummary?: ResourceSummaryDto,
  days: string = '30'
): InactiveRoomsSnapshotResponse | undefined {
  if (roomsSummary && roomsSummary.totalCount === 0) return undefined;
  if (!s) return undefined;

  const roomUsageByWindow = getNestedDict(s, 'RoomUsageByWindow');
  const windowBucket = getCaseInsensitive(roomUsageByWindow, days) as Record<string, unknown> | undefined;
  if (!windowBucket) return undefined;

  const bookedRooms = Number(windowBucket['BookedRooms'] ?? Number.NaN);
  const roomsWithoutBookings = Number(windowBucket['RoomsWithoutBookings'] ?? Number.NaN);
  if (!Number.isFinite(bookedRooms) || !Number.isFinite(roomsWithoutBookings)) return undefined;

  return {
    roomsWithoutBookings,
    bookedRooms,
  };
}

/**
 * Meeting room video snapshot  used vs unused video minutes.
 * Uses RoomUsage summary aggregates.
 */
export function toMeetingRoomVideoSnapshot(
  s: ResourceSummaryDto | undefined,
  roomsSummary?: ResourceSummaryDto,
  days: string = '30',
  roomId: string = 'allrooms'
): MeetingRoomsSnapshotResponse | undefined {
  if (roomsSummary && roomsSummary.totalCount === 0) return undefined;
  if (!s) return undefined;

  if (roomId && roomId.toLowerCase() !== 'allrooms') {
    const usedByRoom = s.aggregates?.[`UsedVideoMinutesByRoom_${days}`] as Record<string, number> | undefined;
    const unusedByRoom = s.aggregates?.[`UnusedVideoMinutesByRoom_${days}`] as Record<string, number> | undefined;

    if (usedByRoom || unusedByRoom) {
      const used = Number(usedByRoom?.[roomId] ?? Number.NaN);
      const unused = Number(unusedByRoom?.[roomId] ?? Number.NaN);
      if (!Number.isFinite(used) || !Number.isFinite(unused)) return undefined;

      return {
        usedVideoMinutes: used,
        unusedVideoMinutes: unused,
      };
    }

    return undefined;
  }

  const roomUsageByWindow = getNestedDict(s, 'RoomUsageByWindow');
  const windowBucket = getCaseInsensitive(roomUsageByWindow, days) as Record<string, unknown> | undefined;
  if (!windowBucket) return undefined;

  const used = Number(windowBucket['UsedVideoMinutes'] ?? Number.NaN);
  const unused = Number(windowBucket['UnusedVideoMinutes'] ?? Number.NaN);
  if (!Number.isFinite(used) || !Number.isFinite(unused)) return undefined;

  return {
    usedVideoMinutes: used,
    unusedVideoMinutes: unused,
  };
}

/**
 * Meeting rooms hours booked  top/bottom 3 rooms by booked minutes.
 * Uses RoomUsage summary + Rooms summary for room names.
 */
export function toMeetingRoomsBookedHours(
  roomUsageSummary: ResourceSummaryDto | undefined,
  roomsSummary: ResourceSummaryDto | undefined,
  rank: string = 'top',
  days: string = '30'
): MeetingsRoomBookedHoursResponse | undefined {
  if (roomsSummary && roomsSummary.totalCount === 0) return undefined;
  if (!roomUsageSummary) return undefined;

  const hierarchyKey = rank === 'top' ? 'TopRooms' : 'BottomRooms';

  // Aggregates path (pipeline stores RoomUsageByWindow in aggregates, not hierarchies)
  const roomUsageByWindow = getNestedDict(roomUsageSummary, 'RoomUsageByWindow');
  const windowBucket = getCaseInsensitive(roomUsageByWindow, days) as Record<string, unknown> | undefined;
  const hierarchyList = windowBucket ? windowBucket[hierarchyKey] : undefined;
  if (Array.isArray(hierarchyList)) {
    const nameMap = roomsSummary ? getNestedDict(roomsSummary, 'RoomNameMap') as Record<string, string> : {};
    return {
      roomHoursBooked: (hierarchyList as Record<string, unknown>[]).map(r => {
        const id = String(r['Id'] ?? r['RoomId'] ?? '');
        return {
          roomName: String(r['Name'] ?? nameMap[id] ?? id),
          roomBookedMinutes: Number(r['BookedMinutes'] ?? r['bookedMinutes'] ?? 0),
          percentage: Number(r['Percentage'] ?? 0),
        };
      }),
    };
  }

  return undefined;
}

/**
 * Meeting rooms booked-hours trend over time.
 * Uses RoomUsage summary time series in summary mode.
 */
export function toMeetingRoomsBookedHoursTrend(
  roomUsageSummary: ResourceSummaryDto | undefined,
  days: string = '30'
): MeetingRoomTrendCompareAgainstResponse | undefined {
  if (!roomUsageSummary) return undefined;

  const normalizedDays = String(Math.max(1, Number(days) || 30));
  const seriesKeys = ['TotalMinutesBooked', 'MinutesBooked', 'BookedMinutes'];

  const trend = toWindowedTrendFromSeriesKeys(roomUsageSummary, seriesKeys, normalizedDays);
  const compareAgainstTrend = toWindowedTrendFromSeriesKeys(
    roomUsageSummary,
    seriesKeys,
    normalizedDays,
    Math.max(1, Number(normalizedDays) || 30),
  );

  if (!trend?.length) return undefined;

  return {
    trend,
    compareAgainstTrend: compareAgainstTrend ?? [],
  };
}

/**
 * Teams & groups activity snapshot.
 * Uses GroupActivity summary.
 *
 * Legacy 'allactivity' counts distinct groups matching criteria in a date window.
 * The pipeline stores ActiveGroups, DeletedGroups, ArchivedGroups as distinct-group
 * counts per status. Their sum = total categorised groups = legacy allactivity.
 */
export function toGroupsActivitySnapshot(
  s: ResourceSummaryDto | undefined,
  dataType: string = 'allactivity',
  days: string = '30',
): GroupsActivitySnapshotResponse | undefined {
  if (!s) return undefined;

  const byWindow = getNestedDict(s, 'GroupActivityByWindow');
  const windowBucket = getCaseInsensitive(byWindow, days) as Record<string, unknown> | undefined;

  const created = Number(windowBucket?.['ActiveGroups'] ?? s.countsByFilter['ActiveGroups'] ?? Number.NaN);
  const deleted = Number(windowBucket?.['DeletedGroups'] ?? s.countsByFilter['DeletedGroups'] ?? Number.NaN);
  const archived = Number(windowBucket?.['ArchivedGroups'] ?? s.countsByFilter['ArchivedGroups'] ?? Number.NaN);

  // Pipeline stores ActiveGroups/DeletedGroups/ArchivedGroups but not AllActivityCount.
  // Their sum equals the legacy allactivity count.
  const allActivity = Number(
    windowBucket?.['AllActivityCount'] ??
      s.countsByFilter['AllActivityCount'] ??
      Number.NaN,
  );

  let value: number;
  switch (dataType) {
    case 'allactivity':
      value = allActivity;
      break;
    case 'created':
      value = created;
      break;
    case 'deleted':
      value = deleted;
      break;
    case 'archived':
      value = archived;
      break;
    default:
      return undefined;
  }

  if (!Number.isFinite(value)) return undefined;

  return {
    value,
    percentage: Number(s.percentageChanges['TotalCount'] ?? 0),
  };
}

/**
 * Org group activity users from pipeline-provided list.
 * Uses TeamHealth summary and preserves pipeline ordering.
 */
export function toOrgGroupActivityUserSnapshot(
  s: ResourceSummaryDto | undefined,
  dataType: string = 'allactivity',
  days: string = '30',
): OrgGroupActivityUserSnapshotResponse | undefined {
  if (!s) return undefined;

  const byWindow = getNestedDict(s, 'OrgActivityByWindow');
  const windowBucket = getCaseInsensitive(byWindow, days);
  const orgList =
    (Array.isArray(windowBucket) ? windowBucket : undefined) ??
    getNestedArray(s, 'OrgActivity');
  if (!orgList || orgList.length === 0) return { activity: [] };

  const ordered = (orgList as Record<string, unknown>[])
    .map((entry) => {
      let activityPerUser: number;
      switch (dataType) {
        case 'messages':
          activityPerUser = Number(entry['MessagesPerUser'] ?? Number.NaN);
          break;
        case 'meetings':
          activityPerUser = Number(entry['MeetingsPerUser'] ?? Number.NaN);
          break;
        default:
          activityPerUser = Number(entry['ActivityPerUser'] ?? Number.NaN);
      }

      return {
        entry,
        activityPerUser,
      };
    })
    .filter((x) => Number.isFinite(x.activityPerUser));

  const activity: { teamName: string; activityPerUser: number; percentage: number }[] = [];
  for (const rankedEntry of ordered) {
    const entry = rankedEntry.entry;
    const name = String(entry['Name'] ?? '');
    activity.push({
      teamName: name,
      activityPerUser: rankedEntry.activityPerUser,
      percentage: Number(entry['Percentage'] ?? 0),
    });
  }

  return { activity };
}

export function toOrgGroupActivityUserMetricSnapshot(
  s: ResourceSummaryDto | undefined,
  dataType: string = 'messages',
  days: string = '30',
): NumberSnapshotResponse | undefined {
  void s;
  void dataType;
  void days;
  return undefined;
}

//  Security self-contained tile adapters 

/**
 * Delegated mailbox data  distinct mailbox count.
 * Uses MailboxDelegationAccesses summary.
 */
export function toDelegatedMailboxSnapshot(
  s: ResourceSummaryDto | undefined,
  days: string = '30',
): DelegatedMailboxResponse | undefined {
  if (!s) return undefined;
  const byDays = getNestedDict(s, 'DelegatedMailboxCountByDays');
  const fromByDays = getWindowedShapeNumber(Object.keys(byDays).length ? byDays : undefined, days);
  return {
    value: Number(fromByDays ?? s.countsByFilter['DelegatedMailboxCount'] ?? 0),
  };
}

//  Utilisation self-contained tile adapters 






